import { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Video, Plus, Copy, Trash2, Calendar, Users, ExternalLink,
  LayoutGrid, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('lbu_admin_token');
const headers = () => ({ Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

export default function AdminMeetings() {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', scheduled_at: '', enable_breakout: false, breakout_rooms: [] });
  const [breakoutName, setBreakoutName] = useState('');

  const fetchMeetings = async () => {
    try {
      const res = await fetch(`${API}/api/admin/meetings`, { headers: headers() });
      if (!res.ok) throw new Error();
      setMeetings(await res.json());
    } catch { toast.error('Failed to load meetings'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchMeetings(); }, []);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      const res = await fetch(`${API}/api/admin/meetings`, {
        method: 'POST', headers: headers(), body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error();
      toast.success('Meeting created!');
      setShowCreate(false);
      setForm({ title: '', description: '', scheduled_at: '', enable_breakout: false, breakout_rooms: [] });
      fetchMeetings();
    } catch { toast.error('Failed to create meeting'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this meeting?')) return;
    try {
      await fetch(`${API}/api/admin/meetings/${id}`, { method: 'DELETE', headers: headers() });
      toast.success('Meeting deleted');
      fetchMeetings();
    } catch { toast.error('Failed to delete meeting'); }
  };

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Join link copied!');
  };

  const addBreakoutRoom = () => {
    if (!breakoutName.trim()) return;
    setForm({ ...form, breakout_rooms: [...form.breakout_rooms, breakoutName.trim()] });
    setBreakoutName('');
  };

  const removeBreakoutRoom = (idx) => {
    setForm({ ...form, breakout_rooms: form.breakout_rooms.filter((_, i) => i !== idx) });
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Not scheduled';

  return (
    <AdminLayout title="Video Meetings">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-white/50 text-sm">Create and manage Jitsi video rooms with breakout support.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-rtb-purple text-black rounded-lg" data-testid="create-meeting-btn">
          <Plus className="w-4 h-4 mr-2" /> New Meeting
        </Button>
      </div>

      {/* Create Meeting Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-charcoal border border-white/10 rounded-2xl p-6 w-full max-w-lg" data-testid="create-meeting-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-oswald text-xl font-bold text-white">New Meeting</h3>
              <button onClick={() => setShowCreate(false)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-white/60 text-sm mb-1.5 block">Meeting Title *</label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  placeholder="Weekly Mastery Session" className="input-dark" data-testid="meeting-title" />
              </div>
              <div>
                <label className="text-white/60 text-sm mb-1.5 block">Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  placeholder="Discuss LLC formation progress and Q&A..."
                  className="w-full bg-white/[0.06] border border-white/[0.08] text-white px-3 py-2 rounded-lg focus:border-rtb-purple focus:outline-none h-20 resize-none"
                  data-testid="meeting-desc" />
              </div>
              <div>
                <label className="text-white/60 text-sm mb-1.5 block">Schedule For</label>
                <Input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({...form, scheduled_at: e.target.value})}
                  className="input-dark" data-testid="meeting-schedule" />
              </div>

              {/* Breakout Rooms */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer mb-3">
                  <input type="checkbox" checked={form.enable_breakout} onChange={e => setForm({...form, enable_breakout: e.target.checked})}
                    className="accent-rtb-purple w-4 h-4" data-testid="enable-breakout" />
                  <span className="text-white/70 text-sm font-medium">Enable Breakout Rooms</span>
                </label>
                {form.enable_breakout && (
                  <div className="pl-6 border-l-2 border-rtb-purple/20 space-y-2">
                    <div className="flex gap-2">
                      <Input value={breakoutName} onChange={e => setBreakoutName(e.target.value)}
                        placeholder="Room name (e.g., Group A)" className="input-dark flex-1" data-testid="breakout-name"
                        onKeyDown={e => e.key === 'Enter' && addBreakoutRoom()} />
                      <Button onClick={addBreakoutRoom} size="sm" className="bg-rtb-purple/20 text-rtb-purple rounded-lg">Add</Button>
                    </div>
                    {form.breakout_rooms.map((room, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 bg-white/[0.04] border border-white/[0.06] rounded-lg">
                        <div className="flex items-center gap-2">
                          <LayoutGrid className="w-3.5 h-3.5 text-rtb-purple" />
                          <span className="text-white/70 text-sm">{room}</span>
                        </div>
                        <button onClick={() => removeBreakoutRoom(i)} className="text-white/30 hover:text-red-400">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={() => setShowCreate(false)} variant="outline" className="flex-1 border-white/10 text-white/60 rounded-lg">Cancel</Button>
                <Button onClick={handleCreate} className="flex-1 bg-rtb-purple text-black rounded-lg" data-testid="confirm-create-meeting">Create Meeting</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Meetings Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-rtb-purple border-t-transparent rounded-full animate-spin" />
        </div>
      ) : meetings.length === 0 ? (
        <div className="text-center py-16">
          <Video className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/40 mb-2">No meetings yet</p>
          <p className="text-white/20 text-sm">Create your first meeting to start hosting sessions.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {meetings.map(m => (
            <div key={m.id} className="p-5 bg-white/[0.03] border border-white/[0.06] rounded-xl hover:border-white/[0.12] transition-all" data-testid={`meeting-${m.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-rtb-purple/10 rounded-xl flex items-center justify-center">
                    <Video className="w-5 h-5 text-rtb-purple" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold">{m.title}</h4>
                    {m.description && <p className="text-white/40 text-xs line-clamp-1">{m.description}</p>}
                  </div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                  m.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' :
                  m.status === 'ended' ? 'bg-white/[0.06] text-white/30' :
                  'bg-blue-500/10 text-blue-400'
                }`}>{m.status}</span>
              </div>

              <div className="flex items-center gap-4 text-white/40 text-xs mb-4">
                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDate(m.scheduled_at)}</span>
                {m.enable_breakout && <span className="flex items-center gap-1"><LayoutGrid className="w-3.5 h-3.5" /> {m.breakout_rooms?.length || 0} Rooms</span>}
              </div>

              {/* Breakout rooms preview */}
              {m.enable_breakout && m.breakout_rooms?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {m.breakout_rooms.map((room, i) => (
                    <span key={i} className="px-2 py-0.5 text-[10px] bg-white/[0.04] border border-white/[0.06] rounded-full text-white/50">{room}</span>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2">
                <a href={m.join_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button size="sm" className="w-full bg-rtb-purple text-black rounded-lg text-xs" data-testid={`join-meeting-${m.id}`}>
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Join Meeting
                  </Button>
                </a>
                <Button size="sm" variant="outline" className="border-white/10 text-white/50 rounded-lg" onClick={() => handleCopy(m.join_url)}>
                  <Copy className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="border-white/10 text-red-400/50 hover:text-red-400 rounded-lg" onClick={() => handleDelete(m.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Jitsi Info */}
      <div className="mt-8 p-4 bg-white/[0.02] border border-white/[0.04] rounded-xl text-white/30 text-xs">
        <p>Powered by <strong className="text-white/50">Jitsi Meet</strong> — free, encrypted video conferencing. No accounts needed for participants.</p>
        <p className="mt-1">Breakout rooms: When enabled, moderator (admin) can move participants between rooms during the session.</p>
      </div>
    </AdminLayout>
  );
}
